import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export function useTenantFeatureAccess() {
  const { user, userRole } = useAuth();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [features, setFeatures] = useState<Record<string, boolean>>({});
  const [tenantSettings, setTenantSettings] = useState<any>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTenantFeatures = async () => {
      if (!user?.id || userRole !== 'customer') {
        setLoading(false);
        return;
      }

      try {
        // Get tenant feature access using secure RPC function
        const { data: accessData, error: accessError } = await supabase.rpc('get_tenant_feature_access_secure', {
          p_user_id: user.id
        });

        if (accessError) {
          console.error('Error fetching tenant features:', accessError);
          setLoading(false);
          return;
        }

        if (accessData && accessData.length > 0) {
          const firstRecord = accessData[0];
          setTenantId(firstRecord.tenant_id);

          // Group feature permissions
          const featuresMap: Record<string, boolean> = {};
          accessData.forEach(record => {
            if (record.feature_key) {
              featuresMap[record.feature_key] = record.is_enabled;
            }
          });

          setFeatures(featuresMap);
          setTenantSettings(firstRecord.tenant_settings || {});
        }
      } catch (error) {
        console.error('Error in fetchTenantFeatures:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTenantFeatures();
  }, [user?.id, userRole]);

  const hasFeature = (featureKey: string): boolean => {
    // If no explicit permission exists, default to enabled for backward compatibility
    return features[featureKey] !== false;
  };

  const hasVisitRewards = (): boolean => {
    // First check if the feature is enabled in permissions
    if (!hasFeature('visit_rewards')) return false;
    
    // Then check tenant settings for visibility
    const visibleTypes = tenantSettings.visible_stamp_types || 'all';
    return visibleTypes === 'all' || visibleTypes === 'visit';
  };

  const hasReferralRewards = (): boolean => {
    // First check if the feature is enabled in permissions
    if (!hasFeature('referral_rewards')) return false;
    
    // Then check tenant settings for visibility
    const visibleTypes = tenantSettings.visible_stamp_types || 'all';
    return visibleTypes === 'all' || visibleTypes === 'referral';
  };

  return {
    tenantId,
    features,
    tenantSettings,
    loading,
    hasFeature,
    // Specific feature checks
    hasVisitRewards: hasVisitRewards(),
    hasReferralRewards: hasReferralRewards(),
  };
}